import { z } from 'zod';

export const APP_STATUS = {
  ACTIVE: 'active',
  DISABLED: 'disabled',
} as const;

export const DEV_HTTP_ALLOWLIST: readonly string[] = [
  'localhost',
  'aid.localhost',
  'tepresta.localhost',
];

export type AppStatus = (typeof APP_STATUS)[keyof typeof APP_STATUS];

const LOCALHOST_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1']);

function extractHostname(url: string): string | null {
  try {
    return new URL(url).hostname.replace(/^\[|\]$/g, '');
  } catch {
    return null;
  }
}

function isHttpsUrl(url: string): boolean {
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
}

export interface ParseAppManifestOptions {
  allowHttpHosts?: string[];
}

function buildManifestSchema(allowHttpHosts: string[] = []) {
  const allowedHttpHosts = new Set([...LOCALHOST_HOSTNAMES, ...allowHttpHosts]);

  const entrySchema = z
    .object({
      id: z.string().min(1, 'App id is required'),
      label: z.string().min(1, 'App label is required'),
      url: z.string().min(1, 'App url is required'),
      icon: z.string().min(1, 'App icon is required'),
      art: z.string().optional(),
      status: z.enum(['active', 'disabled'], {
        errorMap: () => ({ message: 'App status must be "active" or "disabled"' }),
      }),
      external: z.boolean(),
    })
    .superRefine((entry, ctx) => {
      const hostname = extractHostname(entry.url);
      if (!hostname) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['url'],
          message: `App "${entry.id}" URL is not parseable: ${entry.url}`,
        });
        return;
      }

      if (isHttpsUrl(entry.url)) return;

      if (allowedHttpHosts.has(hostname)) return;

      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['url'],
        message: `App "${entry.id}" URL must use HTTPS (HTTP rejected for host: ${hostname})`,
      });
    });

  return z.object({
    schemaVersion: z.number().int().positive('schemaVersion must be a positive integer'),
    apps: z.array(entrySchema).min(1, 'At least one app is required in the manifest'),
  });
}

export const AppManifestSchema = buildManifestSchema();
export type AppManifestEntry = z.infer<typeof AppManifestSchema>['apps'][number];
export type AppManifest = z.infer<typeof AppManifestSchema>;

export function parseAppManifest(input: unknown, options?: ParseAppManifestOptions): AppManifest {
  const schema = buildManifestSchema(options?.allowHttpHosts);
  return schema.parse(input);
}
